// src/components/audio/FlaggedRowAudioButton.js
//
// Tiny play button used in the Flagged-Rows tables of detector dashboards
// (Sentinel Phrases, Probe Depth, Gated Fields). When a reviewer sees a
// suspicious transcript, they should be able to verify it with one click —
// not switch screens, dig through a playlist, and guess which audio column
// produced the transcript.
//
// HOW THE MAPPING WORKS
// =====================
// Whisper transcription, when run through TranscribeModal, names its output
// columns `<audio_column>_transcript`. So:
//
//   informal_learning_skills            (audio column, *.m4a filenames)
//   informal_learning_skills_transcript (text column, generated by Whisper)
//
// To find the audio for a flagged transcript row, we:
//   1. Strip the `_transcript` suffix from the variable name
//   2. Look up that base column in primaryData.columns
//   3. Read the cell value at primaryData.rows[rowIndex][base_column]
//   4. Verify it looks like an audio filename (matches AUDIO_EXT_REGEX)
//
// If any step fails, the component renders nothing — a flagged regular text
// column (typed not transcribed) shouldn't get a play button.
//
// CONVENTION VARIATION
// ====================
// Mwiinga noted: "transcript naming convention changes from programmer to
// another." To handle this gracefully, we try a few known suffix patterns
// before giving up: `_transcript`, `_transcribed`, `_text`, `_whisper`.
// All match against case-insensitive column names.

import React, { useMemo } from 'react';
import InlineAudioCell from './InlineAudioCell';
import RetranscribeButton from './RetranscribeButton';
import { AUDIO_EXT_REGEX } from './audioFormats';

// Known transcript-naming conventions, in order of preference. First match
// wins. All comparisons are case-insensitive.
const TRANSCRIPT_SUFFIXES = [
  '_transcript',
  '_transcribed',
  '_whisper',
  '_text',
];

/**
 * Resolve a flagged variable name to its audio filename.
 *
 * Handles two cases:
 *   1. variable IS a transcript column ("foo_transcript") → look up base "foo"
 *      audio column and read its filename
 *   2. variable IS already an audio column ("foo") whose cell contains an
 *      audio filename (rare, but happens when a Gated-Field rule directly
 *      flags the audio column rather than the transcript)
 *
 * Returns { audioColumn, filename } or null if no audio backing column
 * could be identified.
 *
 * @param {string} variable    The flagged variable name (e.g., "informal_learning_skills_transcript")
 * @param {number} rowIndex    Row index in primaryData.rows
 * @param {object} primaryData { columns: [...], rows: [{...}, ...] }
 */
export const resolveAudioForFlaggedRow = (variable, rowIndex, primaryData) => {
  if (!variable || rowIndex == null || !primaryData?.columns) return null;

  // Build set of column names (case-preserving for lookup; lowercase-set for matching)
  const colNames = primaryData.columns
    .map(c => (typeof c === 'string' ? c : (c?.name || c?.column || '')))
    .filter(Boolean);
  const lowerToActual = new Map(colNames.map(c => [c.toLowerCase(), c]));

  const rows = primaryData.rows || primaryData.data;
  if (!Array.isArray(rows) || rowIndex >= rows.length) return null;

  const row = rows[rowIndex];
  if (!row || typeof row !== 'object') return null;

  const varLower = variable.toLowerCase();

  // CASE 1: variable looks like a transcript column → strip suffix
  for (const suffix of TRANSCRIPT_SUFFIXES) {
    if (varLower.endsWith(suffix)) {
      const baseLower = varLower.slice(0, -suffix.length);
      const audioColumn = lowerToActual.get(baseLower);
      if (audioColumn) {
        const filename = row[audioColumn];
        if (filename && typeof filename === 'string' && AUDIO_EXT_REGEX.test(filename)) {
          return { audioColumn, filename };
        }
      }
      // Suffix matched but base column missing or non-audio — stop trying
      // other suffixes; we found the right convention but not the audio.
      return null;
    }
  }

  // CASE 2: variable IS an audio column (Gated rules can target audio directly)
  // Confirm: column exists, cell value matches an audio extension.
  const directColumn = lowerToActual.get(varLower);
  if (directColumn) {
    const filename = row[directColumn];
    if (filename && typeof filename === 'string' && AUDIO_EXT_REGEX.test(filename)) {
      return { audioColumn: directColumn, filename };
    }
  }

  return null;
};

/**
 * Look up the `text_override` (re-transcribed text) for a flagged row, if any.
 * Used by section components to display the new transcript instead of the
 * stale `response_preview`. Returns null if no override has been set.
 */
export const getTextOverrideForFlaggedRow = (variable, rowIndex, primaryData, transcriptsMeta) => {
  if (!transcriptsMeta?.cells) return null;
  const resolved = resolveAudioForFlaggedRow(variable, rowIndex, primaryData);
  if (!resolved) return null;
  const cell = transcriptsMeta.cells[`${rowIndex}|${resolved.audioColumn}`];
  if (!cell || cell.text_override == null) return null;
  return {
    text:                cell.text_override,
    originalText:        cell.text_original_at_first_override || cell.text || null,
    languageHint:        cell.language_hint,
    languageDetected:    cell.language_detected,
    overrideAt:          cell.override_at,
  };
};

/**
 * FlaggedRowAudioButton — renders a tiny play button for a flagged-row entry,
 * optionally with a confidence pill if Whisper meta is available.
 *
 * Props:
 *   variable:        string  the flagged variable name
 *   rowIndex:        number  row index in primaryData
 *   primaryData:     object  { columns, rows } — the project's primary survey data
 *   audioSettings:   object  { media_dir, ... } — from /api/projects/{id}/audio-settings
 *   tauriApi:        object  { invoke } — Tauri API for reading bytes
 *   isDarkMode:      bool
 *   transcriptsMeta: object  { cells: { "rowIdx|column": {confidence, ...}, ... } }
 *                            from /api/projects/{id}/transcripts-meta
 *
 * Renders nothing if any precondition fails (variable doesn't have a known
 * suffix, audio column missing, file empty, media_dir not configured, not
 * running in Tauri, etc.).
 *
 * Confidence pill renders only when meta is available for this specific
 * row+column. Graceful degradation: if no meta, just the play button shows.
 */

// Confidence pill thresholds — easy to tune after seeing real data
const CONF_THRESHOLDS = {
  HIGH:   80,   // ≥80 → green
  MEDIUM: 60,   // 60-79 → amber, <60 → red
};

const ConfidencePill = ({ confidence, isDarkMode, segments }) => {
  if (confidence == null) return null;

  const tier = confidence >= CONF_THRESHOLDS.HIGH ? 'high'
             : confidence >= CONF_THRESHOLDS.MEDIUM ? 'medium'
             : 'low';

  const colors = {
    high:   { fg: '#059669', bg: 'rgba(5,150,105,0.12)',  bd: 'rgba(5,150,105,0.30)' },
    medium: { fg: '#D97706', bg: 'rgba(217,119,6,0.12)',  bd: 'rgba(217,119,6,0.30)' },
    low:    { fg: '#DC2626', bg: 'rgba(220,38,38,0.12)',  bd: 'rgba(220,38,38,0.30)' },
  }[tier];

  // Build a tooltip with segment-level breakdown
  const tooltip = (() => {
    const head = `Whisper confidence: ${confidence}% (${tier})`;
    if (!Array.isArray(segments) || segments.length === 0) return head;
    const lines = segments.slice(0, 8).map(s => {
      const t = `${formatSec(s.start)}–${formatSec(s.end)}`;
      const c = s.confidence != null ? `${s.confidence}%` : '—';
      const text = (s.text || '').slice(0, 60);
      return `  ${t}  ${c}  ${text}`;
    });
    if (segments.length > 8) lines.push(`  …and ${segments.length - 8} more segments`);
    return [head, '', ...lines].join('\n');
  })();

  return (
    <span
      title={tooltip}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 7px',
        borderRadius: '999px',
        fontSize: '10.5px',
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        color: colors.fg,
        background: colors.bg,
        border: `1px solid ${colors.bd}`,
        cursor: 'help',
        flexShrink: 0,
      }}
    >
      {confidence}%
    </span>
  );
};

const formatSec = (s) => {
  if (!Number.isFinite(s)) return '0:00';
  const total = Math.floor(s);
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

const FlaggedRowAudioButton = ({
  variable, rowIndex, primaryData, audioSettings, tauriApi, isDarkMode,
  transcriptsMeta = null,
  // Ship 2: per-row re-transcribe wiring
  projectId = null,
  userInfo = null,
  audioNotesDoc = null,
  onMetaUpdated = null,
  onAudioNotesUpdated = null,
}) => {
  const resolved = useMemo(
    () => resolveAudioForFlaggedRow(variable, rowIndex, primaryData),
    [variable, rowIndex, primaryData]
  );

  // Look up confidence meta for this specific row + audio column
  const metaEntry = useMemo(() => {
    if (!resolved || !transcriptsMeta?.cells) return null;
    const key = `${rowIndex}|${resolved.audioColumn}`;
    return transcriptsMeta.cells[key] || null;
  }, [resolved, rowIndex, transcriptsMeta]);

  if (!resolved) return null;
  if (!audioSettings?.media_dir) return null;
  if (!tauriApi?.invoke) return null;

  // Re-transcribe is only offered when projectId is known (so we can save).
  // If callers don't pass projectId, the button is hidden and only play
  // and the confidence pill render — graceful degradation for older callers.
  const canRetranscribe = !!projectId;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
      <InlineAudioCell
        filename={resolved.filename}
        mediaDir={audioSettings.media_dir}
        tauriApi={tauriApi}
        isDarkMode={isDarkMode}
        compact={true}
        compactTitle={`Play ${resolved.audioColumn} audio`}
      />
      {metaEntry && (
        <ConfidencePill
          confidence={metaEntry.confidence}
          segments={metaEntry.segments}
          isDarkMode={isDarkMode}
        />
      )}
      {canRetranscribe && (
        <RetranscribeButton
          resolvedAudio={resolved}
          rowIndex={rowIndex}
          audioSettings={audioSettings}
          tauriApi={tauriApi}
          isDarkMode={isDarkMode}
          projectId={projectId}
          primaryData={primaryData}
          transcriptsMeta={transcriptsMeta}
          onMetaUpdated={onMetaUpdated}
          userInfo={userInfo}
          audioNotesDoc={audioNotesDoc}
          onAudioNotesUpdated={onAudioNotesUpdated}
        />
      )}
    </span>
  );
};

export default FlaggedRowAudioButton;